import { PostAdd } from "@mui/icons-material";
import clsx from "clsx";
import { FC, KeyboardEvent, MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import { CheckboxMui, FramerTooltip } from "@ryde/controls";
import {
  CHECKBOX_DEFAULT_SIZE,
  DirectionEnum,
  ICoursesCalendarSlotDto,
  onActivateKeyDown,
} from "@ryde/shared";

import { CoursesCalendarCellCourseName } from "./courses-calendar-cell.course-name";
import { CoursesCalendarCellCourseRouteTypeTooltip } from "./courses-calendar-cell.course-route-type-tooltip";
import { CoursesCalendarCellPassengersCount } from "./courses-calendar-cell.passengers-count";
import { CoursesCalendarCellRideStatus } from "./courses-calendar-cell.ride-status";
import { coursesCalendarRideCardModifierClass } from "./courses-calendar-cell.utils";

interface ICoursesCalendarCellSlotItemProps {
  slot: ICoursesCalendarSlotDto;
  isSelected: boolean;
  onToggleSelected: (slotId: string) => void;
  courseOpenClassName?: string;
  tooltipDisabled?: boolean;
  onOpenCourseClick: (e: MouseEvent | KeyboardEvent, courseId: number) => void;
  onCreateRideClick: (e: MouseEvent, slot: ICoursesCalendarSlotDto) => void;
  onRideOpen: (rideId: number) => void;
}

export const CoursesCalendarCellSlotItem: FC<
  ICoursesCalendarCellSlotItemProps
> = ({
  slot,
  isSelected,
  onToggleSelected,
  courseOpenClassName,
  tooltipDisabled = false,
  onOpenCourseClick,
  onCreateRideClick,
  onRideOpen,
}) => {
  const { t } = useTranslation();
  const directionTooltipContent =
    slot.direction === DirectionEnum.PickUp
      ? t("selectListLabel.DirectionA")
      : t("selectListLabel.DirectionB");

  return (
    <div
      className={clsx(
        "cc-item",
        coursesCalendarRideCardModifierClass(slot),
        isSelected && "cc-item--slot-selected",
      )}
    >
      <div className="cell_row">
        <span className="cc-slot-select-cell">
          <CheckboxMui
            checked={isSelected}
            width={CHECKBOX_DEFAULT_SIZE}
            height={CHECKBOX_DEFAULT_SIZE}
            onCheckboxToggle={() => onToggleSelected(slot.id)}
          />
        </span>
        <FramerTooltip
          content={directionTooltipContent}
          tooltipDisabled={tooltipDisabled}
        >
          <span className="direction_cell">
            {slot.direction === DirectionEnum.PickUp && (
              <SVG_MUI.OvalTo2SVG data-e2e="svg-oval-to" />
            )}
            {slot.direction === DirectionEnum.None && (
              <SVG_MUI.OvalNone2SVG data-e2e="svg-oval-none" />
            )}
            {![DirectionEnum.PickUp, DirectionEnum.None].includes(
              slot.direction,
            ) && <SVG_MUI.OvalBack2SVG data-e2e="svg-oval-back" />}
          </span>
        </FramerTooltip>
        <CoursesCalendarCellCourseRouteTypeTooltip
          slot={slot}
          courseOpenClassName={courseOpenClassName}
          onOpenCourseClick={onOpenCourseClick}
          tooltipDisabled={tooltipDisabled}
        />
        <span
          className={clsx("time_cell", courseOpenClassName)}
          onClick={e => onOpenCourseClick(e, slot.courseId)}
          onKeyDown={onActivateKeyDown(e =>
            onOpenCourseClick(e, slot.courseId),
          )}
          role="button"
          tabIndex={0}
        >
          {slot.timeLabel}
        </span>
        {slot.hasRide ? (
          <CoursesCalendarCellRideStatus
            slot={slot}
            onRideOpen={onRideOpen}
            tooltipDisabled={tooltipDisabled}
          />
        ) : (
          <CoursesCalendarCellCourseName
            slot={slot}
            courseOpenClassName={courseOpenClassName}
            onOpenCourseClick={onOpenCourseClick}
            tooltipDisabled={tooltipDisabled}
          />
        )}
        <span className="cc-row-end">
          {!slot.hasRide && (
            <span className="actions_cell">
              <FramerTooltip
                content={t("course.calendar.createRideLink")}
                tooltipDisabled={tooltipDisabled}
              >
                <PostAdd
                  className="cc-create-ride-icon"
                  fontSize="small"
                  onClick={e => onCreateRideClick(e, slot)}
                />
              </FramerTooltip>
            </span>
          )}
          <span className="cc-passengers-slot">
            <CoursesCalendarCellPassengersCount
              slot={slot}
              tooltipDisabled={tooltipDisabled}
            />
          </span>
        </span>
      </div>
    </div>
  );
};
